import React from 'react';
import {Alert, Box, CircularProgress, Grid, Typography} from '@mui/material';
import {useAuth} from '../auth/AuthContext';
import RoleEditorPanel from './role-management/RoleEditorPanel';
import RoleListPanel from './role-management/RoleListPanel';
import UserRolesTable from './role-management/UserRolesTable';
import useRoleManagementData from './role-management/useRoleManagementData';

export default function RoleManagement() {
    const {hasPermission, user: authUser, updateUser: updateAuthUser, persistence} = useAuth();

    const canReadRoles = hasPermission('role:read');
    const canManageRoles = hasPermission('role:write');
    const canManageUsers = hasPermission('user:write');

    const {
        users,
        loading,
        roleActionPending,
        userActionPending,
        passwordResetPending,
        selectedRoleId,
        editingRole,
        isNewRole,
        error,
        success,
        roleList,
        permissionList,
        handleSelectRole,
        startCreateRole,
        handleRoleFieldChange,
        handlePermissionToggle,
        handleSaveRole,
        handleDeleteRole,
        handleUserRoleChange,
        handleResetPassword,
    } = useRoleManagementData({
        canReadRoles,
        canManageRoles,
        canManageUsers,
        authUser,
        updateAuthUser,
        persistence,
    });

    if (!canReadRoles) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning">Sie verfügen nicht über die notwendigen Berechtigungen.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Typography variant="h4" gutterBottom>
                Rollen &amp; Berechtigungen
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            {loading ? (
                <Box sx={{display: 'flex', justifyContent: 'center', mt: 6}}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <RoleListPanel
                            roleList={roleList}
                            selectedRoleId={selectedRoleId}
                            isNewRole={isNewRole}
                            canManageRoles={canManageRoles}
                            onCreateRole={startCreateRole}
                            onSelectRole={handleSelectRole}
                        />
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <RoleEditorPanel
                            editingRole={editingRole}
                            isNewRole={isNewRole}
                            canManageRoles={canManageRoles}
                            permissionList={permissionList}
                            roleActionPending={roleActionPending}
                            onFieldChange={handleRoleFieldChange}
                            onPermissionToggle={handlePermissionToggle}
                            onSaveRole={handleSaveRole}
                            onDeleteRole={handleDeleteRole}
                        />
                    </Grid>

                    {canManageUsers && (
                        <Grid item xs={12}>
                            <UserRolesTable
                                users={users}
                                roleList={roleList}
                                userActionPending={userActionPending}
                                passwordResetPending={passwordResetPending}
                                onUserRoleChange={handleUserRoleChange}
                                onResetPassword={handleResetPassword}
                            />
                        </Grid>
                    )}
                </Grid>
            )}
        </Box>
    );
}

